import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FileUpload } from "./FileUpload";
import { useState, useEffect } from "react";

const teamMemberSchema = z.object({
  name: z.string().min(1, "Name is required"),
  role_dk: z.string().min(1, "Danish role is required"),
  role_en: z.string().min(1, "English role is required"),
  bio_dk: z.string().optional(),
  bio_en: z.string().optional(),
  photo: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  linkedin: z.string().optional(),
  display_order: z.number().default(0),
  published: z.boolean().default(false),
});

type TeamMemberFormValues = z.infer<typeof teamMemberSchema>;

interface TeamMemberFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TeamMemberFormValues) => void;
  initialData?: any;
  isLoading?: boolean;
}

export const TeamMemberForm = ({ open, onOpenChange, onSubmit, initialData, isLoading }: TeamMemberFormProps) => {
  const [photo, setPhoto] = useState<string | undefined>(initialData?.photo);

  const form = useForm<TeamMemberFormValues>({
    resolver: zodResolver(teamMemberSchema),
    defaultValues: {
      name: "",
      role_dk: "",
      role_en: "",
      bio_dk: "",
      bio_en: "",
      photo: "",
      email: "",
      linkedin: "",
      display_order: 0,
      published: false,
    },
  });

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      form.reset({
        name: initialData.name || "",
        role_dk: initialData.role_dk || "",
        role_en: initialData.role_en || "",
        bio_dk: initialData.bio_dk || "",
        bio_en: initialData.bio_en || "",
        email: initialData.email || "",
        linkedin: initialData.linkedin || "",
        display_order: initialData.display_order || 0,
        published: initialData.published || false,
      });
      setPhoto(initialData.photo);
    } else {
      form.reset({
        name: "",
        role_dk: "",
        role_en: "",
        bio_dk: "",
        bio_en: "",
        photo: "",
        email: "",
        linkedin: "",
        display_order: 0,
        published: false,
      });
      setPhoto(undefined);
    }
  }, [initialData, form]);

  const handleFormSubmit = (data: TeamMemberFormValues) => {
    onSubmit({
      ...data,
      photo,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? "Edit Team Member" : "Add Team Member"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="role_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role (Danish)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role (English)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="bio_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bio (Danish)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bio_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bio (English)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FileUpload
              label="Team Member Photo"
              bucket="team-members"
              accept="image/*"
              maxSizeMB={2}
              currentFile={photo}
              onFileUploaded={(url) => setPhoto(url)}
              onFileRemoved={() => setPhoto(undefined)}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="linkedin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn URL</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="display_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Order</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} onChange={e => field.onChange(parseInt(e.target.value))} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="published"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>Published</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : initialData ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
